/**
 * Shipping setup of a store (eventseasyadmin#299, the admin side of #298's
 * seller-owned couriers): whether customers can order, as the header pill,
 * the banner under it, the sidebar badge, the product page's checklist and
 * the order page show it.
 *
 * The status comes from GET /business/store/info's `shipping_settings`:
 *   { provider: "delhivery" | "bluedart" | "ekart" | "manual" | null,
 *     is_verified, connection_failed, cod_enabled, required, orders_blocked,
 *     grace_until }
 * Until the API sends it, the state is "unknown" and none of the shipping UI
 * shows. "Customers can't place orders" is said only when the API says
 * orders_blocked (checkout refuses them: SHIPPING_REQUIRED on and past the
 * grace date) - otherwise a store without shipping is only asked to set it up.
 */

export const SHIPPING_SETTINGS_PATH = "/shipping-settings";

// Sent on window when the Shipping Settings page saves, so the top bar reloads the store info
export const SHIPPING_CHANGED_EVENT = "mtk:shipping-settings-changed";

export const PROVIDER_LABELS = {
    delhivery: "Delhivery",
    bluedart: "Blue Dart",
    ekart: "Ekart",
    manual: "Manual / self-ship",
};

const DAY = 24 * 60 * 60 * 1000;

const toDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

// Development only: ?shipping_preview=not_set_up | optional | grace | failed | ready | manual
// shows that state without the API (kept for the tab; ?shipping_preview=off ends it)
const PREVIEW_KEY = "shippingPreview";
const previewSettings = (name, now) => ({
    not_set_up: { provider: null, required: true, orders_blocked: true },
    grace: { provider: null, required: true, orders_blocked: false, grace_until: new Date(now.getTime() + 16 * DAY).toISOString() },
    failed: { provider: "delhivery", is_verified: true, connection_failed: true, orders_blocked: true, required: true },
    optional: { provider: null, required: false, orders_blocked: false },
    ready: { provider: "delhivery", is_verified: true },
    manual: { provider: "manual" },
}[name] || null);

const devPreview = (now) => {
    if (process.env.NODE_ENV !== "development") return null;
    try {
        const fromUrl = new URLSearchParams(window.location.search).get("shipping_preview");
        if (fromUrl === "off") sessionStorage.removeItem(PREVIEW_KEY);
        else if (fromUrl) sessionStorage.setItem(PREVIEW_KEY, fromUrl);
        return previewSettings(sessionStorage.getItem(PREVIEW_KEY), now);
    } catch (e) {
        return null;
    }
};

/**
 * @param {object} store - the /business/store/info result
 * @param {Date} [now]
 * @returns {{state: "unknown"|"not_set_up"|"optional"|"grace"|"failed"|"ready", provider?: string|null,
 *   providerLabel?: string, isCourier?: boolean, graceUntil?: Date, daysLeft?: number,
 *   failedAt?: Date|null, ordersBlocked?: boolean, codEnabled?: boolean}}
 *   Manual / self-ship counts as set up; a courier only once verified, and not
 *   after its connection failed. Not set up: "not_set_up" when checkout
 *   refuses the store's orders, "grace" before the grace date, "optional"
 *   when checkout doesn't require shipping at all.
 */
export const shippingStatus = (store, now = new Date()) => {
    const settings = devPreview(now) || (store && store.shipping_settings);
    if (!settings || typeof settings !== "object") return { state: "unknown" };

    const provider = PROVIDER_LABELS[settings.provider] ? settings.provider : null;
    const ordersBlocked = settings.orders_blocked === true;
    const base = {
        provider,
        providerLabel: provider ? PROVIDER_LABELS[provider] : "",
        isCourier: !!provider && provider !== "manual",
        ordersBlocked,
        // Cash on Delivery for the whole store (Shipping Settings); on until set
        codEnabled: settings.cod_enabled !== false,
    };
    if (provider === "manual") return { ...base, state: "ready" };

    // The public store info only says that it failed; when is the seller's own (GET /shipping/settings)
    const failedAt = toDate(settings.failed_at);
    if (provider && (failedAt || settings.connection_failed)) return { ...base, state: "failed", failedAt };
    if (provider && settings.is_verified) return { ...base, state: "ready" };

    const graceUntil = toDate(settings.grace_until);
    if (!ordersBlocked && graceUntil && graceUntil > now) {
        return { ...base, state: "grace", graceUntil, daysLeft: Math.max(1, Math.ceil((graceUntil - now) / DAY)) };
    }
    return { ...base, state: ordersBlocked ? "not_set_up" : "optional" };
};

// red: customers can't order now (or the courier stopped working); amber: they
// can, but shipping still needs setting up; green: set up
export const shippingTone = (state) => ({
    not_set_up: "red",
    failed: "red",
    grace: "amber",
    optional: "amber",
    ready: "green",
}[state] || null);

// "Remind me tomorrow" on the grace banner: hidden for a day, per store
const snoozeKey = (businessId) => `shippingBannerSnoozedUntil:${businessId || ""}`;

export const isBannerSnoozed = (businessId, now = Date.now()) => {
    try {
        return Number(localStorage.getItem(snoozeKey(businessId))) > now;
    } catch (e) {
        return false;
    }
};

export const snoozeBanner = (businessId, now = Date.now()) => {
    try {
        localStorage.setItem(snoozeKey(businessId), String(now + DAY));
    } catch (e) {
        // Private mode: the banner just comes back on the next page
    }
};

/**
 * A product's weight in kg, from its free-text shipping.weight ("500g",
 * "1kg", "1.2 kg", "250 gm"), or null when it has no unit or isn't a weight -
 * read the same way as add_gift.js's shipping estimate.
 */
export const parseWeightKg = (value) => {
    const match = String(value ?? "").trim().toLowerCase()
        .match(/^(\d+(?:\.\d+)?)\s*(kg|kgs|kilograms?|g|gm|gms|grams?)$/);
    if (!match) return null;
    const amount = parseFloat(match[1]);
    if (!(amount > 0)) return null;
    return match[2].startsWith("k") ? amount : amount / 1000;
};

export const formatKg = (kg) => String(Number(kg.toFixed(3)));

// Digital products aren't shipped, so they need no weight - the API's rule
// (is_digital, or product_type "digital"; uniquegiftapi cartPricing.isDigitalProduct)
export const isDigitalProduct = (product) => !!product && (product.is_digital === true || product.product_type === "digital");
export const needsWeight = (product) => !!product && !isDigitalProduct(product);

const boxOf = (d = {}) => {
    const dims = [d.length, d.width, d.height].map(Number);
    return dims.every((n) => n > 0) ? dims.join("×") : "";
};

/**
 * "1.2 kg · 30×20×12 cm", "0.5 kg" without a full box size, or null without
 * a weight. Weights per variant (shipping.variant_weights, uniquegiftapi
 * includes/productShipping.js): "0.27–0.52 kg · per Size" once every
 * variant has one.
 */
export const weightAndSize = (product) => {
    const shipping = (product && product.shipping) || {};
    const rows = shipping.variant_weights || [];
    if (rows.length) {
        const kgs = rows.map((row) => parseWeightKg(row.weight));
        if (kgs.some((kg) => kg == null)) return null;
        const low = Math.min(...kgs);
        const high = Math.max(...kgs);
        const range = low === high ? formatKg(low) : `${formatKg(low)}–${formatKg(high)}`;
        const per = shipping.weight_per_combination ? "per variant" : `per ${shipping.weight_variant_type || "variant"}`;
        return `${range} kg · ${per}`;
    }
    const kg = parseWeightKg(shipping.weight);
    if (kg == null) return null;
    const box = boxOf(shipping.dimensions);
    return `${formatKg(kg)} kg${box ? ` · ${box} cm` : ""}`;
};

/**
 * { kg, box } of one ordered unit: its variant's row when the product has
 * weights per variant, else the product's. Same as the API's itemShipping.
 */
export const itemShipping = (product, selected = []) => {
    const shipping = (product && product.shipping) || {};
    const rows = shipping.variant_weights || [];
    if (rows.length) {
        const chosen = new Map((selected || []).map((v) => [String(v.type), String(v.value)]));
        const key = shipping.weight_per_combination
            ? (product.variants || []).map((v) => chosen.get(String(v.type))).filter(Boolean).join(" / ")
            : chosen.get(String(shipping.weight_variant_type)) || "";
        const row = rows.find((r) => r.key === key);
        if (row) return { kg: parseWeightKg(row.weight), box: row.dimensions || {} };
    }
    return { kg: parseWeightKg(shipping.weight), box: shipping.dimensions || {} };
};

export const productsMissingWeight = (products) =>
    (products || []).filter((product) => needsWeight(product) && !weightAndSize(product));

/**
 * The product page's "Get your store ready to take orders" steps, in order.
 * The courier step is the onboarding modal's "Step n of 4".
 */
export const setupSteps = ({ productCount, paymentConnected, shipping, storeLive }) => [
    { key: "products", done: productCount > 0 },
    { key: "payment", done: !!paymentConnected },
    { key: "courier", done: !!shipping && shipping.state === "ready" },
    { key: "store", done: !!storeLive },
];
